import { useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MoroccanLoader from './MoroccanLoader';

interface AuthGuardProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  loginPath?: string;
  children: ReactNode;
}

export default function AuthGuard({
  isAuthenticated,
  isLoading,
  loginPath = '/login',
  children,
}: AuthGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Capture path + query string so the user returns to the exact page
      // (e.g. /app/procurement/explore?filter=open) after logging in. Matches
      // the 401 handlers, which encode pathname + search.
      navigate(`${loginPath}?redirect=${encodeURIComponent(location.pathname + location.search)}`, {
        replace: true,
      });
    }
  }, [isLoading, isAuthenticated, navigate, location.pathname, location.search, loginPath]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-guard-loader">
        <MoroccanLoader size="lg" message={t('common.loading')} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
